import { Fragment } from 'react'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSetPositionBlocked } from '@/hooks/useStorage'
import { isOccupiable } from '@/lib/storageTree'
import { nodeDetails, type PanelTab } from '@/lib/storageUnitView'
import { cn } from '@/lib/utils'
import type { ApiStorageNode, StorageCapacity, StorageTreeNode } from '@/types/storage'
import { KeptAtBadge } from '../KeptAtBadge'
import { NodeContentsPanel } from '../NodeContentsPanel'
import { StorageAuditPanel } from '../StorageAuditPanel'

const TRIGGER =
  'rounded-none border-b-2 border-transparent px-2.5 py-3 text-sm text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-foreground'

const TAB_LABEL: Record<PanelTab, string> = {
  contents: 'Contents',
  selected: 'Selected',
  history: 'History',
}

/**
 * The unit page's right-hand panel, as tabs: what is in the selection
 * (Contents, Inventory) or what the selection is (Selected, Equipment), and
 * what has happened to it (History). Settings is a modal the page opens.
 *
 * `tabs` is what this user may open — the page works that out from features
 * and permissions, so a tab that would only 403 is never offered.
 */
export function UnitSidePanel({
  node,
  nodes,
  chain,
  tab,
  tabs,
  onTabChange,
  occupiedIds,
  canManage,
  placing,
  onPlacingChange,
  canBlock,
  onEditSettings,
  zones,
  capacity,
  onJump,
  onAddInside,
}: {
  node: ApiStorageNode
  /** The unit's containers, as loaded. */
  nodes: StorageTreeNode[]
  /** Root→node, for what it is kept at. */
  chain: ApiStorageNode[]
  tab: PanelTab
  tabs: PanelTab[]
  onTabChange: (tab: PanelTab) => void
  occupiedIds: ReadonlySet<string>
  canManage: boolean
  /**
   * The put-away is open for this (free) slot — from the header's "＋ Put
   * stock here" or the Contents tab's own; both open the same panel.
   */
  placing: boolean
  onPlacingChange: (placing: boolean) => void
  /** Block / Put back in service on Selected — `inventory.manage`. */
  canBlock: boolean
  /**
   * Open Settings for this node — on Selected (Equipment) and atop Contents
   * (Inventory). Omitted when there is nothing in it this user may do.
   */
  onEditSettings?: () => void
  /** The picking zones, to name the node's zone on Selected. */
  zones: { id: string; name: string }[]
  /** The node's own capacity, when known — Places and Blocked on Selected. */
  capacity: StorageCapacity | null
  /** Select a level of the node's path, from Selected's breadcrumb. */
  onJump: (node: ApiStorageNode) => void
  /** "＋ Add rack or box" (the unit's extend wizard), for a container. */
  onAddInside?: () => void
}) {
  const isSlot = isOccupiable(node.kind)

  return (
    <Tabs value={tab} onValueChange={(v) => onTabChange(v as PanelTab)} className='flex min-h-0 flex-col'>
      <TabsList className='h-auto w-full justify-start gap-1 rounded-none border-0 border-b bg-transparent p-0 px-2'>
        {tabs.map((t) => (
          <TabsTrigger key={t} value={t} className={TRIGGER}>
            {TAB_LABEL[t]}
          </TabsTrigger>
        ))}
      </TabsList>

      {tabs.includes('contents') && (
        <TabsContent value='contents' className='mt-0 space-y-3 p-4'>
          {/* The panel edits the selection; the header's ⚙ Settings edits the unit. */}
          {onEditSettings && (
            <div className='flex justify-end'>
              <Button size='sm' variant='outline' onClick={onEditSettings}>
                ⚙ Edit settings
              </Button>
            </div>
          )}
          <NodeContentsPanel
            node={node}
            nodes={nodes}
            occupiedIds={occupiedIds}
            canManage={canManage}
            placing={placing}
            onPlacingChange={onPlacingChange}
          />
        </TabsContent>
      )}

      {tabs.includes('selected') && (
        <TabsContent value='selected' className='mt-0 p-4'>
          <SelectedCard
            key={node.id}
            node={node}
            chain={chain}
            zones={zones}
            capacity={capacity}
            onJump={onJump}
            canBlock={isSlot && canBlock}
            onAddInside={isSlot ? undefined : onAddInside}
            onEditSettings={onEditSettings}
          />
        </TabsContent>
      )}

      {tabs.includes('history') && (
        <TabsContent value='history' className='mt-0 p-4'>
          {/* On a container this is everything beneath it: a rack has no
              events of its own, so an exact-node trail would read as "nothing
              happened" while every slot inside it was being filled. */}
          <StorageAuditPanel
            key={node.id}
            nodeId={node.id}
            subtree={!isSlot}
            title={isSlot ? 'History of this slot' : 'History of everything in here'}
            defaultOpen
          />
        </TabsContent>
      )}
    </Tabs>
  )
}

const STATUS_TONE = {
  ok: 'border-emerald-300 bg-emerald-50 text-emerald-800',
  // Striped like a blocked cell on the map, so the two read as one signal.
  blocked:
    'border-rose-300 text-rose-800 bg-[repeating-linear-gradient(45deg,theme(colors.rose.50)_0_5px,theme(colors.rose.100)_5px_10px)]',
} as const

/**
 * Equipment's view of the selection (or the unit, with nothing selected):
 * what it is and how it is set up — never what is stored in it.
 *
 * Header (code, status, a breadcrumb that selects each level), a details
 * list from `nodeDetails`, then the actions this user may take, as one group.
 */
function SelectedCard({
  node,
  chain,
  zones,
  capacity,
  onJump,
  canBlock,
  onAddInside,
  onEditSettings,
}: {
  node: ApiStorageNode
  chain: ApiStorageNode[]
  zones: { id: string; name: string }[]
  capacity: StorageCapacity | null
  onJump: (node: ApiStorageNode) => void
  canBlock: boolean
  onAddInside?: () => void
  onEditSettings?: () => void
}) {
  const setBlocked = useSetPositionBlocked()
  const details = nodeDetails({ chain, zones, capacity })
  const actions = [canBlock, !!onAddInside, !!onEditSettings].filter(Boolean).length

  return (
    <div className='space-y-4'>
      <header className='space-y-1.5'>
        <div className='flex flex-wrap items-center gap-2'>
          <h3 className='text-xl font-semibold tracking-tight'>{node.code}</h3>
          <span
            className={cn(
              'rounded-md border px-2 py-0.5 text-xs font-semibold',
              node.blocked ? STATUS_TONE.blocked : STATUS_TONE.ok,
            )}
          >
            {node.blocked ? 'Blocked' : 'In service'}
          </span>
        </div>
        {node.name && <p className='text-sm text-muted-foreground'>{node.name}</p>}
        {chain.length > 1 && (
          <nav aria-label='Where it is' className='flex flex-wrap items-center gap-1 text-xs text-muted-foreground'>
            {chain.map((level, i) => (
              <Fragment key={level.id}>
                {i > 0 && <span aria-hidden>›</span>}
                {i === chain.length - 1 ? (
                  <span className='font-medium text-foreground'>{level.code}</span>
                ) : (
                  <button type='button' className='hover:text-primary hover:underline' onClick={() => onJump(level)}>
                    {level.code}
                  </button>
                )}
              </Fragment>
            ))}
          </nav>
        )}
      </header>

      <dl className='divide-y rounded-md border text-sm'>
        {details.map((row) => (
          <div key={row.key} className='flex items-center justify-between gap-3 px-3 py-2'>
            <dt className='text-muted-foreground'>{row.label}</dt>
            <dd className='flex min-w-0 flex-wrap items-center justify-end gap-1.5 text-right'>
              {row.key === 'keptAt' ? (
                <KeptAtBadge condition={row.condition ?? null} />
              ) : (
                <span className='font-medium'>{row.value}</span>
              )}
              {row.note && <span className='text-xs text-muted-foreground'>{row.note}</span>}
            </dd>
          </div>
        ))}
      </dl>

      {actions > 0 && (
        <div className='space-y-2 border-t pt-4'>
          <div className={cn('grid gap-2', actions > 1 && 'grid-cols-2')}>
            {canBlock && (
              <Button
                variant='outline'
                className='w-full'
                disabled={setBlocked.isPending}
                onClick={() => setBlocked.mutate({ id: node.id, blocked: !node.blocked })}
              >
                {node.blocked ? 'Put back in service' : 'Block slot'}
              </Button>
            )}
            {onAddInside && (
              <Button variant='outline' className='w-full' onClick={onAddInside}>
                ＋ Add rack or box
              </Button>
            )}
            {onEditSettings && (
              <Button variant='outline' className='w-full' onClick={onEditSettings}>
                ⚙ Edit settings
              </Button>
            )}
          </div>
          {canBlock && (
            <p className='text-xs text-muted-foreground'>
              Blocked slots are left out of capacity — nothing can be stored here.
            </p>
          )}
          {/* A slot holding stock cannot be blocked; the server says so. */}
          {setBlocked.error && <p className='text-sm text-destructive'>{String(setBlocked.error.message)}</p>}
        </div>
      )}
    </div>
  )
}
